import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';

import AnalyticsCard from './components/AnalyticsCard';
import CategoryManager from './components/CategoryManager';
import RestaurantProfileCard from './components/RestaurantProfileCard';
import DishStatsPanel from './components/DishStatsPanel';
import SupabaseHealthPanel from './components/SupabaseHealthPanel';
import PromotionsManager from './components/PromotionsManager';
import PaymentSection from './components/PaymentSection';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import ErrorToast from '../../components/ui/ErrorToast';

import { useLanguage } from '../../i18n/LanguageContext';
import { MANAGEMENT_CREDENTIALS } from '../../constants/auth';
import { fetchDashboardDishStats, clearDashboardDishStatsCache } from '../../services/dishStatsService';
import {
  fetchRestaurantProfile,
  updateRestaurantProfile,
  fetchCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  fetchDishes,
} from '../../services/menuDataService';
import { fetchPromotions } from '../../services/promotionsService';

// Helper to generate unique toast IDs
let _toastId = 0;
const newToastId = () => `toast-${++_toastId}`;

const RestaurantAdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('adminAuthenticated') === 'true';
  });
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Promotions state
  const [promotions, setPromotions] = useState([]);

  // Error toasts state
  const [errorToasts, setErrorToasts] = useState([]);
  const addToast = (message, type = 'error') =>
    setErrorToasts(prev => [...prev, { id: newToastId(), message, type }]);
  const dismissToast = (id) =>
    setErrorToasts(prev => prev?.filter(t => t?.id !== id));

  // Real-time analytics state
  const [totalAR, setTotalAR] = useState(null);
  const [totalQR, setTotalQR] = useState(null);
  const [popularDishCount, setPopularDishCount] = useState(null);
  const [arChange, setArChange] = useState(null);
  const [qrChange, setQrChange] = useState(null);
  const [popularChange, setPopularChange] = useState(null);
  const [dishStats, setDishStats] = useState([]);
  const [dishStatsLoading, setDishStatsLoading] = useState(
    () => sessionStorage.getItem('adminAuthenticated') === 'true'
  );
  const [dishStatsError, setDishStatsError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams?.get('tab') || 'dashboard';
  });

  const statsLoadStartedRef = useRef(false);

  const loadDashboardStats = async (force = false) => {
    setDishStatsLoading(true);
    setDishStatsError(null);
    try {
      const { rows, totals } = await fetchDashboardDishStats({ force });
      setDishStats((rows || [])?.filter(d => d?.dish_id !== 'restaurant'));
      setTotalAR(totals?.totalAR ?? 0);
      setTotalQR(totals?.totalQR ?? 0);
      setPopularDishCount(totals?.popular ?? 0);
      setArChange(totals?.arChange);
      setQrChange(totals?.qrChange);
      setPopularChange(totals?.popularChange);
    } catch (err) {
      console.error('Dashboard stats load error:', err?.message || err);
      setDishStatsError('Impossible de charger les statistiques.');
      setTotalAR(0);
      setTotalQR(0);
      setPopularDishCount(0);
    } finally {
      setDishStatsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      statsLoadStartedRef.current = false;
      return;
    }
    if (statsLoadStartedRef.current) return;
    statsLoadStartedRef.current = true;
    loadDashboardStats();
  }, [isAuthenticated]);

  // ── Promotions (Supabase) ──────────────────────────────────────────────────
  const loadPromotions = () => {
    fetchPromotions()?.then(setPromotions)?.catch(() => addToast('Impossible de charger les promotions.', 'warning'));
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadPromotions();
  }, [isAuthenticated]);

  // ── Restaurant Profile (Supabase) ──────────────────────────────────────────
  const [restaurantProfile, setRestaurantProfile] = useState({
    name: 'The Gourmet Kitchen',
    tagline: 'Culinary Excellence Since 2020',
    cuisine: 'Contemporary American',
    phone: '+1 (555) 123-4567',
    email: 'info@gourmetkitchen.com',
    address: '123 Culinary Street, Food District, NY 10001',
    hours: 'Mon-Sun: 11:00 AM - 10:00 PM',
    currency: 'USD',
    logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f1e3e5de-1767798794108.png',
    logoAlt: 'Elegant restaurant logo featuring stylized chef hat and crossed utensils in gold on dark background',
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchRestaurantProfile()?.then(setRestaurantProfile)?.catch(() => addToast('Impossible de charger le profil du restaurant.', 'warning'));
  }, [isAuthenticated]);

  // ── Categories (Supabase) ──────────────────────────────────────────────────
  const [categories, setCategories] = useState([
    { id: 'appetizers', name: t('appetizers'), itemCount: 0 },
    { id: 'mains', name: t('mainCourse'), itemCount: 0 },
    { id: 'desserts', name: t('desserts'), itemCount: 0 },
    { id: 'beverages', name: t('beverages'), itemCount: 0 },
  ]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchCategories()?.then(setCategories)?.catch(() => addToast('Impossible de charger les catégories.', 'warning'));
  }, [isAuthenticated]);

  const analyticsData = [
  {
    title: t('totalQRScans'),
    value: totalQR !== null ? totalQR?.toLocaleString() : "—",
    change: qrChange?.value ?? null,
    changeType: qrChange?.type || "positive",
    icon: "QrCode",
    iconColor: "var(--color-primary)",
    subtitle: t('thisMonth')
  },
  {
    title: t('arEngagements'),
    value: totalAR !== null ? totalAR?.toLocaleString() : "—",
    change: arChange?.value ?? null,
    changeType: arChange?.type || "positive",
    icon: "Scan",
    iconColor: "var(--color-accent)",
    subtitle: t('totalARViews')
  },
  {
    title: t('popularDishes'),
    value: popularDishCount !== null ? String(popularDishCount) : "—",
    change: popularChange?.value ?? null,
    changeType: popularChange?.type || "positive",
    icon: "TrendingUp",
    iconColor: "var(--color-success)",
    subtitle: t('highEngagement')
  }];


  const handleAddCategory = async (name) => {
    try {
      const newCat = await addCategory(name);
      setCategories(prev => [...prev, newCat]);
    } catch (e) {
      addToast(e?.message || 'Échec de la sauvegarde de la catégorie.', 'error');
    }
  };

  const handleEditCategory = async (id, name) => {
    try {
      await updateCategory(id, name);
      setCategories(prev => prev?.map(cat => cat?.id === id ? { ...cat, name } : cat));
    } catch (e) {
      addToast('Échec de la mise à jour de la catégorie.', 'error');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await deleteCategory(id);
      setCategories(prev => prev?.filter(cat => cat?.id !== id));
    } catch (e) {
      addToast('Échec de la suppression de la catégorie.', 'error');
    }
  };

  const handleUpdateProfile = async (updatedProfile) => {
    const previousProfile = restaurantProfile;
    setRestaurantProfile(updatedProfile);
    try {
      await updateRestaurantProfile(updatedProfile);
    } catch (e) {
      setRestaurantProfile(previousProfile);
      addToast(e?.message || 'Échec de la sauvegarde du profil du restaurant.', 'error');
    }
  };

  const handleOpenContentEditor = () => {
    navigate('/management-content-editor');
  };

  const handleExportData = async () => {
    try {
      const dishes = await fetchDishes();
      const blob = new Blob([JSON.stringify(dishes, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `menu-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      addToast('Menu exporté.', 'info');
    } catch (e) {
      addToast(e?.message || 'Échec de l\'export.', 'error');
    }
  };

  const handleGenerateQR = () => {
    navigate('/qr-code-generator');
  };

  const handleLogin = (e) => {
    e?.preventDefault();
    if (
    loginForm?.username === MANAGEMENT_CREDENTIALS?.username &&
    loginForm?.password === MANAGEMENT_CREDENTIALS?.password)
    {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuthenticated', 'true');
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
    setLoginForm({ username: '', password: '' });
    statsLoadStartedRef.current = false;
    clearDashboardDishStatsCache();
  };

  const handleRefreshDishStats = () => {
    loadDashboardStats(true);
  };

  return (
    <div className="restaurant-admin-dashboard min-h-screen bg-background">
      {!isAuthenticated ?
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="w-full max-w-sm bg-card border border-border rounded-xl shadow-warm p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Lock" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground">Admin Access</h1>
              <p className="text-sm text-muted-foreground mt-1">Sign in to manage your restaurant</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Username</label>
                <Input
                type="text"
                placeholder="Enter username"
                value={loginForm?.username}
                onChange={(e) => setLoginForm((prev) => ({ ...prev, username: e?.target?.value }))}
                required />
              
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Password</label>
                <Input
                type="password"
                placeholder="Enter password"
                value={loginForm?.password}
                onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e?.target?.value }))}
                required />
              
              </div>
              {loginError &&
            <p className="text-sm text-red-500 text-center">{loginError}</p>
            }
              <button
              type="submit"
              className="w-full py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors">
              
                Sign In
              </button>
              <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              
                ← Back to Menu
              </button>
            </form>
          </div>
        </div> :

      <>
      <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activeTab={activeTab}
          onTabChange={setActiveTab} />
        
      <div
          className={`dashboard-content transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`
          }>
          
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12 mt-16 lg:mt-0">
          <div className="dashboard-header mb-6 md:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  {activeTab === 'payment' ? 'Paiement' : 'Restaurant Dashboard'}
                </h1>
                <p className="text-sm md:text-base font-caption text-muted-foreground">
                  {activeTab === 'payment' ?'Gérez votre abonnement et vos paiements' :'Gérez votre menu numérique et suivez l\'engagement des clients'}
                </p>
              </div>
              <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                  
                <Icon name="LogOut" size={16} />
                Logout
              </button>
            </div>
          </div>

          {activeTab === 'payment' ? (
            <PaymentSection />
          ) : (
            <>
              <div className="analytics-section mb-6 md:mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground font-caption">{t('thisMonth')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {analyticsData?.map((data, index) =>
                    <AnalyticsCard key={index} {...data} />
                    )}
                </div>
              </div>

              <div className="mb-6 md:mb-8">
                <PromotionsManager
                  promotions={promotions}
                  onRefresh={loadPromotions}
                />
              </div>

              <div className="mb-6 md:mb-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleOpenContentEditor}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium shadow-warm hover:opacity-90 transition-opacity"
                >
                  <Icon name="Edit3" size={18} />
                  Éditeur de contenu (plats & AR)
                </button>
                <button
                  type="button"
                  onClick={handleExportData}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                >
                  <Icon name="Download" size={18} />
                  Exporter le menu (JSON)
                </button>
                <button
                  type="button"
                  onClick={handleGenerateQR}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                >
                  <Icon name="QrCode" size={18} />
                  Générateur QR
                </button>
              </div>

              <div className="mb-6 md:mb-8">
                <SupabaseHealthPanel onToast={addToast} />
              </div>

              <div className="grid grid-cols-1 gap-6 md:gap-8 mb-6 md:mb-8">
                <div className="space-y-6 md:space-y-8">
                  <CategoryManager
                      categories={categories}
                      onAddCategory={handleAddCategory}
                      onEditCategory={handleEditCategory}
                      onDeleteCategory={handleDeleteCategory} />

                  <RestaurantProfileCard
                      profile={restaurantProfile}
                      onUpdate={handleUpdateProfile} />
                </div>
              </div>

              <DishStatsPanel
                stats={dishStats}
                loading={dishStatsLoading}
                error={dishStatsError}
                onRefresh={handleRefreshDishStats}
              />
            </>
          )}
            
        </div>
      </div>
      {/* Error Toasts */}
      <ErrorToast toasts={errorToasts} onDismiss={dismissToast} />
        
        </>
      }
    </div>);

};

export default RestaurantAdminDashboard;